import { AUTH_ERRORS } from "@/common/errors";
import {
  reAuthenticate,
  resolveMFAChallenge,
  verify,
} from "@/services/auth2FA";
import { FirebaseError } from "firebase/app";
import type { MultiFactorError, MultiFactorResolver } from "firebase/auth";
import { useRef, useState } from "react";

export enum Verify2FAStep {
  ENTER_PASSWORD = "ENTER_PASSWORD",
  ENTER_SMS = "ENTER_SMS",
  COMPLETED = "COMPLETED",
}

export const useVerifyIdentity2FA = (): {
  handleVerify: (password: string, code: string) => Promise<void>;
  resetVerifyState: () => void;
  loading: boolean;
  error: string | null;
  step: Verify2FAStep;
} => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<Verify2FAStep>(Verify2FAStep.ENTER_PASSWORD);
  const resolved = useRef<{
    resolver: MultiFactorResolver;
    verificationId: string;
  } | null>(null);

  const resetVerifyState = (): void => {
    setLoading(false);
    setError(null);
    setStep(Verify2FAStep.ENTER_PASSWORD);
  };

  const handleVerify = async (
    password: string,
    code: string
  ): Promise<void> => {
    setLoading(true);
    setError(null);
    if (step == Verify2FAStep.ENTER_PASSWORD) {
      try {
        await reAuthenticate(password);
        setStep(Verify2FAStep.COMPLETED);
      } catch (error) {
        if (error instanceof FirebaseError) {
          if (error.code === AUTH_ERRORS.MULTI_FACTOR_REQUIRED) {
            const result = await resolveMFAChallenge(error as MultiFactorError);
            resolved.current = result;
            setStep(Verify2FAStep.ENTER_SMS);
          }
          setError(error.code);
        }
      } finally {
        setLoading(false);
      }
    }
    if (step == Verify2FAStep.ENTER_SMS) {
      try {
        if (resolved.current) {
          await verify(
            resolved.current.resolver,
            resolved.current.verificationId,
            code
          );
          setStep(Verify2FAStep.COMPLETED);
        }
      } catch (error) {
        if (error instanceof FirebaseError) {
          setError(error.code);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  return {
    resetVerifyState,
    handleVerify,
    step,
    loading,
    error,
  };
};
